import type { MisconceptionDefinition } from './types'

/** TOPO — Topografie misconcepties. */
export const TOPO_MISCONCEPTIONS: MisconceptionDefinition[] = [
  // ───────────────────────────────────────────────────────────
  // NL — Steden ↔ Provincie (verwarring buurprovincies)
  // ───────────────────────────────────────────────────────────
  {
    id: 'topo.buurprovincie-verward',
    label: 'Buurprovincie verward',
    module: 'Topo',
    description:
      'Leerling kiest een aangrenzende provincie omdat de stad dicht bij de grens ligt (bijv. Almere in Noord-Holland i.p.v. Flevoland).',
    typicalFamilies: ['TOPO-NL-STEDEN-REGIO', 'TOPO-NL-STEDEN', 'TOPO-NL-PROV'],
  },
  {
    id: 'topo.holland-noord-zuid-verward',
    label: 'Noord-Holland ↔ Zuid-Holland verward',
    module: 'Topo',
    description:
      'Leerling wisselt Noord-Holland en Zuid-Holland om bij steden in de Randstad.',
    typicalFamilies: ['TOPO-NL-STEDEN-REGIO', 'TOPO-NL-STEDEN'],
  },
  {
    id: 'topo.utrecht-stad-vs-provincie',
    label: 'Provincie Utrecht ↔ stad verward',
    module: 'Topo',
    description:
      'Leerling kiest Utrecht voor steden die in een aangrenzende provincie liggen (Hilversum, Naarden) omdat ze dicht bij de stad Utrecht liggen.',
    typicalFamilies: ['TOPO-NL-STEDEN-REGIO', 'TOPO-NL-STEDEN'],
  },
  {
    id: 'topo.zuid-vs-noord-confused',
    label: 'Zuidelijke vs. noordelijke provincie verward',
    module: 'Topo',
    description:
      'Leerling kiest een te ver zuidelijke of noordelijke provincie zonder rekening te houden met de werkelijke ligging.',
    typicalFamilies: ['TOPO-NL-STEDEN-REGIO', 'TOPO-NL-STEDEN'],
  },
  {
    id: 'topo.oost-vs-west-confused',
    label: 'Oostelijke vs. westelijke provincie verward',
    module: 'Topo',
    description:
      'Leerling kiest een te ver oostelijke of westelijke provincie (bijv. Gelderland i.p.v. Overijssel) bij steden in het binnenland.',
    typicalFamilies: ['TOPO-NL-STEDEN-REGIO', 'TOPO-NL-STEDEN'],
  },
  {
    id: 'topo.regio-binnen-provincie-verward',
    label: 'Regio binnen provincie verward',
    module: 'Topo',
    description:
      'Leerling kiest de verkeerde regio binnen een provincie (bijv. Kop van Overijssel i.p.v. Twente).',
    typicalFamilies: ['TOPO-NL-STEDEN-REGIO'],
  },
  {
    id: 'topo.stadsdeel-als-eigen-stad',
    label: 'Stadsdeel als zelfstandige stad behandeld',
    module: 'Topo',
    description:
      'Leerling behandelt een stadsdeel of buurgemeente (bijv. Scheveningen) als zelfstandige stad in plaats van als onderdeel van een grotere stad (Den Haag).',
    typicalFamilies: ['TOPO-NL-STEDEN-REGIO', 'TOPO-NL-STEDEN'],
  },
  {
    id: 'topo.streek-vs-provincie-verward',
    label: 'Streeknaam met provincienaam verward',
    module: 'Topo',
    description:
      'Leerling denkt dat een streeknaam (Westland, Veluwe, Twente, Achterhoek, Betuwe, Gooi) een eigen provincie is, in plaats van een gebied binnen een provincie.',
    typicalFamilies: ['TOPO-NL-STEDEN-REGIO'],
  },
  {
    id: 'topo.veenkolonien-grens-verward',
    label: 'Veenkolonien aan verkeerde provincie gekoppeld',
    module: 'Topo',
    description:
      'Leerling plaatst plaatsen uit de Groninger of Drentse Veenkolonien (Veendam, Stadskanaal, Hoogeveen) aan de verkeerde kant van de provinciegrens.',
    typicalFamilies: ['TOPO-NL-STEDEN-REGIO'],
  },
  {
    id: 'topo.hanze-stad-provincie-verward',
    label: 'Hanzestad aan verkeerde provincie gekoppeld',
    module: 'Topo',
    description:
      'Leerling verwart Hanzesteden in Overijssel (Kampen, Deventer, Zwolle) met die in Gelderland (Zutphen, Harderwijk) omdat ze allemaal aan de IJssel liggen.',
    typicalFamilies: ['TOPO-NL-STEDEN-REGIO'],
  },

  // ───────────────────────────────────────────────────────────
  // Hoofdsteden / capitals
  // ───────────────────────────────────────────────────────────
  {
    id: 'topo.hoofdstad-verward-met-grootste',
    label: 'Hoofdstad verward met grootste stad',
    module: 'Topo',
    description:
      'Leerling kiest de grootste stad als hoofdstad of andersom (bijv. Amsterdam i.p.v. Den Haag als regeringsstad).',
    typicalFamilies: ['TOPO-NL-PROV', 'TOPO-EU-HOOFDST', 'TOPO-WERELD-HOOFDST'],
  },
  {
    id: 'topo.hoofdstad-buurland-verward',
    label: 'Hoofdstad van buurland verward',
    module: 'Topo',
    description:
      'Leerling koppelt een hoofdstad aan het verkeerde land (vaak een buurland).',
    typicalFamilies: ['TOPO-EU-HOOFDST', 'TOPO-WERELD-HOOFDST'],
  },

  // ───────────────────────────────────────────────────────────
  // Wateren / rivieren / zeeën
  // ───────────────────────────────────────────────────────────
  {
    id: 'topo.rivier-verkeerde-richting',
    label: 'Rivier in verkeerde richting',
    module: 'Topo',
    description:
      'Leerling kiest een rivier die door het verkeerde land of de verkeerde regio stroomt.',
    typicalFamilies: [
      'TOPO-NL-WATER',
      'TOPO-EU-RIVIEREN',
      'TOPO-WERELD-RIVIER',
    ],
  },
  {
    id: 'topo.zee-vs-meer-verward',
    label: 'Zee vs. binnenwater verward',
    module: 'Topo',
    description:
      'Leerling verwart een zee met een binnenwater (bijv. IJsselmeer als zee), of andersom.',
    typicalFamilies: ['TOPO-NL-WATER', 'TOPO-EU-ZEEEN'],
  },

  // ───────────────────────────────────────────────────────────
  // Eilanden / kust
  // ───────────────────────────────────────────────────────────
  {
    id: 'topo.eiland-verkeerde-keten',
    label: 'Eiland aan verkeerde keten gekoppeld',
    module: 'Topo',
    description:
      'Leerling koppelt een Waddeneiland aan een Zeeuws eiland of andersom.',
    typicalFamilies: ['TOPO-NL-EILANDEN'],
  },

  // ───────────────────────────────────────────────────────────
  // Buurlanden / EU
  // ───────────────────────────────────────────────────────────
  {
    id: 'topo.buurland-verkeerde-zijde',
    label: 'Buurland aan verkeerde zijde',
    module: 'Topo',
    description:
      'Leerling plaatst een buurland aan de verkeerde kant (oost/west/noord/zuid).',
    typicalFamilies: ['TOPO-NL-BUURLANDEN', 'TOPO-EU-BUURLANDEN'],
  },
  {
    id: 'topo.land-verkeerd-continent',
    label: 'Land op verkeerd continent',
    module: 'Topo',
    description: 'Leerling plaatst een land op het verkeerde continent.',
    typicalFamilies: ['TOPO-WERELD-LANDEN', 'TOPO-WERELD-CONT'],
  },

  // ───────────────────────────────────────────────────────────
  // Landschap / gebergte / klimaat
  // ───────────────────────────────────────────────────────────
  {
    id: 'topo.gebergte-verkeerd-land',
    label: 'Gebergte aan verkeerd land gekoppeld',
    module: 'Topo',
    description:
      'Leerling koppelt een gebergte aan het verkeerde land of de verkeerde regio.',
    typicalFamilies: ['TOPO-EU-GEBERGTE', 'TOPO-WERELD-GEBERGTE'],
  },
  {
    id: 'topo.klimaatzone-verward',
    label: 'Klimaatzone verward',
    module: 'Topo',
    description:
      'Leerling koppelt een land of regio aan de verkeerde klimaatzone.',
    typicalFamilies: ['TOPO-KLIMAAT', 'TOPO-EU-KLIMAAT', 'TOPO-WERELD-KLIMAAT'],
  },
  {
    id: 'topo.klimaat-tegenovergesteld',
    label: 'Tegenovergestelde klimaatzone gekozen',
    module: 'Topo',
    description:
      'Leerling kiest de tegenovergestelde klimaatzone (bijv. polair waar tropisch hoort, of tropisch waar polair hoort).',
    typicalFamilies: ['TOPO-KLIMAAT', 'TOPO-EU-KLIMAAT', 'TOPO-WERELD-KLIMAAT'],
  },
  {
    id: 'topo.klimaat-kenmerk-verward',
    label: 'Klimaatkenmerk verward',
    module: 'Topo',
    description:
      'Leerling verwart kenmerken van klimaatzones (temperatuur, neerslag, seizoenen) tussen verschillende zones.',
    typicalFamilies: ['TOPO-KLIMAAT', 'TOPO-EU-KLIMAAT', 'TOPO-WERELD-KLIMAAT'],
  },
  {
    id: 'topo.klimaat-breedtegraad-niet-toegepast',
    label: 'Breedtegraad-kennis niet toegepast',
    module: 'Topo',
    description:
      'Leerling past de relatie tussen breedtegraad en klimaat niet toe (bijv. Nederland als tropisch of polair classificeren).',
    typicalFamilies: ['TOPO-KLIMAAT', 'TOPO-EU-KLIMAAT'],
  },
  {
    id: 'topo.klimaat-neerslag-verkeerd-ingeschat',
    label: 'Neerslagniveau verkeerd ingeschat',
    module: 'Topo',
    description:
      'Leerling schat het neerslagniveau van een klimaatzone verkeerd in (bijv. tropisch als droog, of polair als nat).',
    typicalFamilies: ['TOPO-KLIMAAT', 'TOPO-WERELD-KLIMAAT'],
  },
  {
    id: 'topo.klimaat-bergen-als-hoofdfactor',
    label: 'Bergen gezien als hoofdfactor voor klimaat',
    module: 'Topo',
    description:
      'Leerling denkt dat hoogte van bergen de klimaatzone bepaalt in plaats van breedtegraad.',
    typicalFamilies: ['TOPO-KLIMAAT'],
  },
  {
    id: 'topo.klimaat-rivieren-als-hoofdfactor',
    label: 'Rivieren gezien als hoofdfactor voor klimaat',
    module: 'Topo',
    description:
      'Leerling denkt dat het aantal of de aanwezigheid van rivieren het klimaat bepaalt in plaats van breedtegraad.',
    typicalFamilies: ['TOPO-KLIMAAT'],
  },
  {
    id: 'topo.klimaat-landgrootte-als-hoofdfactor',
    label: 'Landgrootte gezien als hoofdfactor voor klimaat',
    module: 'Topo',
    description:
      'Leerling denkt dat de grootte van een land het klimaat bepaalt in plaats van breedtegraad.',
    typicalFamilies: ['TOPO-KLIMAAT'],
  },
  {
    id: 'topo.klimaat-pool-met-seizoenen',
    label: 'Poolklimaat verward met vier seizoenen',
    module: 'Topo',
    description:
      'Leerling denkt dat het poolklimaat vier duidelijke seizoenen heeft (kenmerk van gematigd klimaat).',
    typicalFamilies: ['TOPO-KLIMAAT', 'TOPO-WERELD-KLIMAAT'],
  },
  {
    id: 'topo.klimaat-pool-met-tropisch',
    label: 'Poolklimaat verward met tropisch',
    module: 'Topo',
    description:
      'Leerling denkt dat het poolklimaat warm en regenrijk is (kenmerken van tropisch klimaat).',
    typicalFamilies: ['TOPO-KLIMAAT', 'TOPO-WERELD-KLIMAAT'],
  },
  {
    id: 'topo.klimaat-pool-met-woestijn',
    label: 'Poolklimaat verward met woestijnklimaat',
    module: 'Topo',
    description:
      'Leerling verwart het poolklimaat (constant koud) met woestijnklimaat (droog met grote temperatuurverschillen).',
    typicalFamilies: ['TOPO-KLIMAAT', 'TOPO-WERELD-KLIMAAT'],
  },
  {
    id: 'topo.landschapstype-verward',
    label: 'Landschapstype verward',
    module: 'Topo',
    description:
      'Leerling kiest het verkeerde landschapstype (bijv. veen i.p.v. duin) bij een Nederlands gebied.',
    typicalFamilies: ['TOPO-NL-LANDSCHAP'],
  },
  {
    id: 'topo.edinburgh-vk-verward',
    label: 'Verwart Edinburgh met VK-hoofdstad',
    module: 'Topo',
    description:
      'Leerling verwart Edinburgh (Schotland) met de hoofdstad van het Verenigd Koninkrijk.',
    typicalFamilies: ['TOPO-EU-HOOFDST'],
  },
  {
    id: 'topo.grote-stad-is-hoofdstad',
    label: 'Denkt grootste stad = hoofdstad',
    module: 'Topo',
    description:
      'Leerling denkt onterecht dat de grootste of bekendste stad altijd de hoofdstad is.',
    typicalFamilies: ['TOPO-EU-HOOFDST'],
  },
  {
    id: 'topo.dublin-vk-verward',
    label: 'Verwart Dublin met VK',
    module: 'Topo',
    description:
      'Leerling verwart Dublin (Ierland) met het Verenigd Koninkrijk.',
    typicalFamilies: ['TOPO-EU-HOOFDST'],
  },
  {
    id: 'topo.thessaloniki-griekenland',
    label: 'Kiest op-een-na-grootste stad',
    module: 'Topo',
    description:
      'Leerling kiest een andere grote stad in plaats van de hoofdstad.',
    typicalFamilies: ['TOPO-EU-HOOFDST'],
  },
  {
    id: 'topo.kreta-als-stad',
    label: 'Verwart eiland met stad',
    module: 'Topo',
    description:
      'Leerling kiest een eiland (bijv. Kreta) in plaats van een stad.',
    typicalFamilies: ['TOPO-EU-HOOFDST'],
  },
  {
    id: 'topo.istanbul-griekenland',
    label: 'Verwart Istanbul met Griekenland',
    module: 'Topo',
    description:
      'Leerling plaatst Istanbul in Griekenland in plaats van Turkije.',
    typicalFamilies: ['TOPO-EU-HOOFDST'],
  },
  {
    id: 'topo.scandinavie-oslo-zweden',
    label: 'Verwart Oslo met Zweden',
    module: 'Topo',
    description:
      'Leerling verwart de hoofdstad van Noorwegen met die van Zweden.',
    typicalFamilies: ['TOPO-EU-HOOFDST'],
  },
  {
    id: 'topo.scandinavie-helsinki-zweden',
    label: 'Verwart Helsinki met Zweden',
    module: 'Topo',
    description:
      'Leerling verwart de hoofdstad van Finland met die van Zweden.',
    typicalFamilies: ['TOPO-EU-HOOFDST'],
  },
  {
    id: 'topo.scandinavie-kopenhagen-zweden',
    label: 'Verwart Kopenhagen met Zweden',
    module: 'Topo',
    description:
      'Leerling verwart de hoofdstad van Denemarken met die van Zweden.',
    typicalFamilies: ['TOPO-EU-HOOFDST'],
  },
  {
    id: 'topo.niet-vraag-verkeerd-gelezen',
    label: 'Leest NIET-vraag verkeerd',
    module: 'Topo',
    description:
      'Leerling leest over het woordje NIET heen en kiest de hoofdstad.',
    typicalFamilies: ['TOPO-EU-HOOFDST'],
  },
  {
    id: 'topo.niet-vraag-berlijn',
    label: 'Leest NIET-vraag verkeerd (Berlijn)',
    module: 'Topo',
    description: 'Leerling leest over het woordje NIET heen en kiest Berlijn.',
    typicalFamilies: ['TOPO-EU-HOOFDST'],
  },
  {
    id: 'topo.niet-vraag-rome',
    label: 'Leest NIET-vraag verkeerd (Rome)',
    module: 'Topo',
    description: 'Leerling leest over het woordje NIET heen en kiest Rome.',
    typicalFamilies: ['TOPO-EU-HOOFDST'],
  },
  {
    id: 'topo.buurland-via-buurland',
    label: 'Verwart buurland van buurland met buurland',
    module: 'Topo',
    description:
      'Leerling denkt dat een land dat aan een buurland grenst, ook een direct buurland is.',
    typicalFamilies: ['TOPO-NL-BUURLANDEN'],
  },
  {
    id: 'topo.windstreken-omgedraaid',
    label: 'Windstreken omgedraaid',
    module: 'Topo',
    description: 'Leerling verwart de windstreken (bijv. oost met west).',
    typicalFamilies: ['TOPO-NL-BUURLANDEN'],
  },
  {
    id: 'topo.land-grootte-verward',
    label: 'Grootte van landen verward',
    module: 'Topo',
    description: 'Leerling schat de relatieve grootte van landen verkeerd in.',
    typicalFamilies: ['TOPO-NL-BUURLANDEN'],
  },
  {
    id: 'topo.water-rijn-als-zuid',
    label: 'Plaatst Rijn in zuiden',
    module: 'Topo',
    description:
      'Leerling plaatst de Rijn in het zuiden van Nederland in plaats van het midden.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-waal-met-maas-verward',
    label: 'Verwart Waal met Maas',
    module: 'Topo',
    description: 'Leerling verwart de Waal met de Maas.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-ijssel-met-maas-verward',
    label: 'Verwart IJssel met Maas',
    module: 'Topo',
    description: 'Leerling verwart de IJssel met de Maas.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-ijssel-tak-met-rijn-verward',
    label: 'Verwart IJssel-tak met Rijn',
    module: 'Topo',
    description: 'Leerling verwart de IJssel-tak met de Rijn als geheel.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-waddenzee-als-uitmonding',
    label: 'Plaatst uitmonding in Waddenzee',
    module: 'Topo',
    description:
      'Leerling denkt dat een rivier in de Waddenzee uitmondt in plaats van de Noordzee.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-middellandse-zee-misplaatst',
    label: 'Plaatst rivier in Middellandse Zee',
    module: 'Topo',
    description:
      'Leerling plaatst een Nederlandse rivier in de Middellandse Zee.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-maas-in-gelderland',
    label: 'Plaatst Maas in Gelderland',
    module: 'Topo',
    description:
      'Leerling plaatst de Maas in Gelderland in plaats van Limburg/Brabant.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-schelde-in-midden',
    label: 'Plaatst Schelde in midden-NL',
    module: 'Topo',
    description:
      'Leerling plaatst de Schelde in midden-Nederland in plaats van het zuidwesten.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-eems-als-gelderland',
    label: 'Plaatst Eems in Gelderland',
    module: 'Topo',
    description:
      'Leerling plaatst de Eems in Gelderland in plaats van Groningen.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-markermeer-met-ijsselmeer-verward',
    label: 'Verwart Markermeer met IJsselmeer',
    module: 'Topo',
    description: 'Leerling verwart het Markermeer met het grotere IJsselmeer.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-waddenzee-als-zuiderzee',
    label: 'Verwart Waddenzee met Zuiderzee',
    module: 'Topo',
    description: 'Leerling verwart de Waddenzee met de voormalige Zuiderzee.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-veluwemeer-met-ijsselmeer-verward',
    label: 'Verwart Veluwemeer met IJsselmeer',
    module: 'Topo',
    description: 'Leerling verwart het Veluwemeer met het IJsselmeer.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-maas-als-duitse-grens',
    label: 'Verwart Maas met Duitse grens',
    module: 'Topo',
    description: 'Leerling denkt dat de Maas de grens met Duitsland vormt.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-waal-als-grens',
    label: 'Denkt Waal vormt grens',
    module: 'Topo',
    description: 'Leerling denkt dat de Waal een landsgrens vormt.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-lek-als-grens',
    label: 'Denkt Lek vormt grens',
    module: 'Topo',
    description: 'Leerling denkt dat de Lek een landsgrens vormt.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-maas-naar-zeeland',
    label: 'Stuurt Maas direct naar Zeeland',
    module: 'Topo',
    description:
      'Leerling stuurt de Maas direct naar Zeeland zonder door Brabant te gaan.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-maas-start-zuid-holland',
    label: 'Slaat Brabant over bij Maas',
    module: 'Topo',
    description: 'Leerling denkt dat de Maas direct in Zuid-Holland begint.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-maas-in-nijmegen',
    label: 'Plaatst Maas in Nijmegen',
    module: 'Topo',
    description: 'Leerling plaatst de Maas in Nijmegen in plaats van de Waal.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-rijn-tak-naam-gemist',
    label: 'Noemt Rijn i.p.v. specifieke tak',
    module: 'Topo',
    description:
      'Leerling noemt de Rijn in plaats van de specifieke tak (bijv. Waal of IJssel).',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-ijssel-in-nijmegen',
    label: 'Plaatst IJssel in Nijmegen',
    module: 'Topo',
    description:
      'Leerling plaatst de IJssel in Nijmegen in plaats van de Waal.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-maas-als-langste',
    label: 'Denkt Maas is langste',
    module: 'Topo',
    description:
      'Leerling denkt dat de Maas de langste rivier van Nederland is.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-waal-als-zelfstandige-rivier',
    label: 'Ziet Waal als zelfstandige rivier',
    module: 'Topo',
    description:
      'Leerling ziet de Waal als een aparte rivier in plaats van een tak van de Rijn.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-ijssel-als-langste',
    label: 'Denkt IJssel is langste',
    module: 'Topo',
    description:
      'Leerling denkt dat de IJssel de langste rivier van Nederland is.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-waddenzee-als-hoofdzee',
    label: 'Verwart Waddenzee met Noordzee',
    module: 'Topo',
    description: 'Leerling verwart de Waddenzee met de Noordzee als hoofdzee.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-oostzee-misplaatst',
    label: 'Plaatst Oostzee bij NL',
    module: 'Topo',
    description:
      'Leerling plaatst de Oostzee bij Nederland in plaats van Scandinavië.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-rijn-als-noord-tak',
    label: 'Noemt Rijn i.p.v. noord-tak',
    module: 'Topo',
    description:
      'Leerling noemt de Rijn in plaats van de specifieke noord-tak (IJssel).',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-maas-in-overijssel',
    label: 'Plaatst Maas in Overijssel',
    module: 'Topo',
    description:
      'Leerling plaatst de Maas in Overijssel in plaats van het zuiden.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-waal-naar-ijsselmeer',
    label: 'Stuurt Waal naar IJsselmeer',
    module: 'Topo',
    description: 'Leerling denkt dat de Waal naar het IJsselmeer stroomt.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-deltawerken-als-afsluitdijk',
    label: 'Verwart Deltawerken met Afsluitdijk',
    module: 'Topo',
    description:
      'Leerling verwart de Deltawerken in Zeeland met de Afsluitdijk.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-houtribdijk-als-afsluit',
    label: 'Verwart Houtribdijk met Afsluitdijk',
    module: 'Topo',
    description: 'Leerling verwart de Houtribdijk met de Afsluitdijk.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-oosterscheldekering-als-afsluit',
    label: 'Verwart stormvloedkering met Afsluitdijk',
    module: 'Topo',
    description: 'Leerling verwart de Oosterscheldekering met de Afsluitdijk.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-maas-met-rijn-bron-verward',
    label: 'Verwart Maas-bron met Rijn-bron',
    module: 'Topo',
    description:
      'Leerling denkt dat de Maas uit Duitsland komt (zoals de Rijn).',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-bron-met-binnenkomst-verward',
    label: 'Verwart bronland met binnenkomstland',
    module: 'Topo',
    description:
      'Leerling geeft het land waar de rivier ontspringt in plaats van waar hij Nederland binnenkomt.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-geografie-afstand-misgeschat',
    label: 'Mist geografische afstand',
    module: 'Topo',
    description:
      'Leerling schat de geografische afstand verkeerd in (bijv. Spanje als buurland).',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-maas-als-rijn-tak',
    label: 'Ziet Maas als Rijn-tak',
    module: 'Topo',
    description: 'Leerling denkt dat de Maas een tak van de Rijn is.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-schelde-als-rijn-tak',
    label: 'Ziet Schelde als Rijn-tak',
    module: 'Topo',
    description: 'Leerling denkt dat de Schelde een tak van de Rijn is.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-eems-als-rijn-tak',
    label: 'Ziet Eems als Rijn-tak',
    module: 'Topo',
    description: 'Leerling denkt dat de Eems een tak van de Rijn is.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-rijn-in-rotterdam',
    label: 'Noemt Rijn i.p.v. Nieuwe Maas',
    module: 'Topo',
    description:
      'Leerling noemt de Rijn in Rotterdam in plaats van de lokale naam Nieuwe Maas.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-waal-in-rotterdam',
    label: 'Plaatst Waal in Rotterdam',
    module: 'Topo',
    description:
      'Leerling plaatst de Waal in Rotterdam in plaats van Nijmegen.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-ijssel-in-rotterdam',
    label: 'Plaatst IJssel in Rotterdam',
    module: 'Topo',
    description:
      'Leerling plaatst de IJssel in Rotterdam in plaats van het oosten.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-adam-rijnkanaal-verward',
    label: 'Verwart Amsterdam-Rijnkanaal met zee-verbinding',
    module: 'Topo',
    description:
      'Leerling verwart het binnenkanaal met de verbinding naar de Noordzee.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-nieuwe-waterweg-adam',
    label: 'Verwart Rotterdamse zee-verbinding met Amsterdamse',
    module: 'Topo',
    description:
      'Leerling verwart de Nieuwe Waterweg (Rotterdam) met het Noordzeekanaal (Amsterdam).',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-markermeer-als-kanaal',
    label: 'Verwart meer met kanaal',
    module: 'Topo',
    description: 'Leerling verwart een meer met een kanaal.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-rijn-door-adam',
    label: 'Plaatst Rijn door Amsterdam',
    module: 'Topo',
    description: 'Leerling denkt dat de Rijn door Amsterdam stroomt.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-vecht-door-adam',
    label: 'Verwart Vecht met Amstel',
    module: 'Topo',
    description: 'Leerling verwart de Vecht met de Amstel in Amsterdam.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-zaan-door-adam',
    label: 'Verwart Zaan met Amstel',
    module: 'Topo',
    description: 'Leerling verwart de Zaan met de Amstel in Amsterdam.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-ooster-als-antwerpen-route',
    label: 'Verwart Oosterschelde met Westerschelde',
    module: 'Topo',
    description:
      'Leerling denkt dat de Oosterschelde de route naar Antwerpen is.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-grevelingen-als-route',
    label: 'Verwart afgesloten meer met open zeearm',
    module: 'Topo',
    description:
      'Leerling denkt dat het Grevelingenmeer de route naar Antwerpen is.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-haringvliet-als-route',
    label: 'Plaatst Antwerpen-route in Zuid-Holland',
    module: 'Topo',
    description:
      'Leerling denkt dat het Haringvliet de route naar Antwerpen is.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-wester-als-kering',
    label: 'Verwart open Westerschelde met afsluitbare Oosterschelde',
    module: 'Topo',
    description:
      'Leerling denkt dat de stormvloedkering in de Westerschelde ligt.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-hollands-diep-als-kering',
    label: 'Plaatst Deltakering te ver landinwaarts',
    module: 'Topo',
    description:
      'Leerling denkt dat de stormvloedkering in het Hollands Diep ligt.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-markermeer-als-kering',
    label: 'Plaatst Zeeuwse kering in Flevoland',
    module: 'Topo',
    description:
      'Leerling denkt dat de stormvloedkering in het Markermeer ligt.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-ijsselmeer-in-zuid',
    label: 'Plaatst IJsselmeer in zuiden',
    module: 'Topo',
    description: 'Leerling plaatst het IJsselmeer in het zuiden van Nederland.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-waddenzee-in-zuid',
    label: 'Plaatst Waddenzee in zuiden',
    module: 'Topo',
    description: 'Leerling plaatst de Waddenzee in het zuiden van Nederland.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-veluwemeer-in-zuid',
    label: 'Plaatst Veluwemeer in zuiden',
    module: 'Topo',
    description: 'Leerling plaatst het Veluwemeer in het zuiden van Nederland.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-noordzeekanaal-rotterdam',
    label: 'Verwart Amsterdamse zee-verbinding met Rotterdamse',
    module: 'Topo',
    description: 'Leerling denkt dat het Noordzeekanaal bij Rotterdam ligt.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-adam-rijnkanaal-rotterdam',
    label: 'Verwart binnenkanaal met zee-verbinding',
    module: 'Topo',
    description:
      'Leerling denkt dat het Amsterdam-Rijnkanaal de zee-verbinding van Rotterdam is.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-westerschelde-rotterdam',
    label: 'Verwart Zeeuwse zeearm met Rotterdamse waterweg',
    module: 'Topo',
    description:
      'Leerling denkt dat de Westerschelde de zee-verbinding van Rotterdam is.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-ijsselmeer-als-markermeer',
    label: 'Verwart IJsselmeer met Markermeer',
    module: 'Topo',
    description:
      'Leerling verwart het noordelijke IJsselmeer met het zuidelijke Markermeer.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-veluwemeer-als-markermeer',
    label: 'Verwart oost-randmeer met west-randmeer',
    module: 'Topo',
    description: 'Leerling verwart het Veluwemeer met het Markermeer.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-grevelingen-als-markermeer',
    label: 'Plaatst Zeeuws meer bij Flevoland',
    module: 'Topo',
    description: 'Leerling verwart het Grevelingenmeer met het Markermeer.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-markermeer-als-veluwemeer',
    label: 'Verwart west-randmeer met oost-randmeer',
    module: 'Topo',
    description: 'Leerling verwart het Markermeer met het Veluwemeer.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-ijsselmeer-als-veluwemeer',
    label: 'Verwart groot meer met klein randmeer',
    module: 'Topo',
    description: 'Leerling verwart het IJsselmeer met het Veluwemeer.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-hollandsdiep-als-veluwemeer',
    label: 'Plaatst zuid-water bij Flevoland',
    module: 'Topo',
    description: 'Leerling verwart het Hollands Diep met het Veluwemeer.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-maas-als-nederrijn-voortzetting',
    label: 'Verwart Maas met Nederrijn-voortzetting',
    module: 'Topo',
    description:
      'Leerling denkt dat de Maas de voortzetting van de Nederrijn is.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-ijssel-als-lek',
    label: 'Verwart noord-tak met west-tak',
    module: 'Topo',
    description: 'Leerling verwart de IJssel met de Lek.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-dommel-als-lek',
    label: 'Verwart Brabants riviertje met grote Rijn-tak',
    module: 'Topo',
    description: 'Leerling verwart de Dommel met de Lek.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-maas-in-eindhoven',
    label: 'Plaatst Maas in Eindhoven',
    module: 'Topo',
    description: 'Leerling denkt dat de Maas door Eindhoven stroomt.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-vecht-in-brabant',
    label: 'Verwart Vecht met Dommel',
    module: 'Topo',
    description: 'Leerling denkt dat de Vecht door Brabant stroomt.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-amstel-in-brabant',
    label: 'Verwart Amstel met Dommel',
    module: 'Topo',
    description: 'Leerling denkt dat de Amstel door Brabant stroomt.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-markermeer-als-zoutwater',
    label: 'Verwart zoetwatermeer met zoutwatermeer',
    module: 'Topo',
    description: 'Leerling denkt dat het Markermeer zout water is.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-veluwemeer-als-zoutwater',
    label: 'Verwart oost-randmeer met zuidwest-meer',
    module: 'Topo',
    description: 'Leerling denkt dat het Veluwemeer zout water is.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-waddenzee-als-meer',
    label: 'Verwart open zee met afgesloten meer',
    module: 'Topo',
    description: 'Leerling denkt dat de Waddenzee een afgesloten meer is.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-ijssel-als-vecht',
    label: 'Verwart twee Overijsselse rivieren',
    module: 'Topo',
    description: 'Leerling verwart de IJssel met de Vecht.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-eems-in-overijssel',
    label: 'Plaatst Eems in Overijssel',
    module: 'Topo',
    description:
      'Leerling plaatst de Eems in Overijssel in plaats van Groningen.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-rijn-in-overijssel',
    label: 'Plaatst Rijn in Overijssel',
    module: 'Topo',
    description:
      'Leerling plaatst de Rijn in Overijssel in plaats van Gelderland.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-eb-als-overstroming',
    label: 'Verwart eb met overstroming',
    module: 'Topo',
    description: 'Leerling denkt dat eb een overstroming betekent.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-eb-als-vorst',
    label: 'Verwart getij met temperatuur',
    module: 'Topo',
    description: 'Leerling denkt dat eb met bevriezen te maken heeft.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  {
    id: 'topo.water-eb-als-verzoeting',
    label: 'Verwart getij met zoutgehalte',
    module: 'Topo',
    description: 'Leerling denkt dat eb het water zoet maakt.',
    typicalFamilies: ['TOPO-NL-WATER'],
  },
  // ───────────────────────────────────────────────────────────
  // TOPO-EU-LAND — landvormen en regio's van Europa
  // ───────────────────────────────────────────────────────────
  {
    id: 'topo.land-iberisch-vs-apennijns',
    label: 'Iberisch ↔ Apennijns Schiereiland verward',
    module: 'Topo',
    description:
      'Leerling verwart het Iberisch Schiereiland (Spanje/Portugal) met het Apennijns Schiereiland (Italië, de laars).',
    typicalFamilies: ['TOPO-EU-LAND'],
  },
  {
    id: 'topo.land-grillige-kust-vs-laars',
    label: 'Grillige kust ↔ laarsvorm verward',
    module: 'Topo',
    description:
      'Leerling verwart de grillige kustlijn met veel eilanden (Griekenland) met de laarsvorm van Italië.',
    typicalFamilies: ['TOPO-EU-LAND'],
  },
  {
    id: 'topo.land-adriatisch-vs-laars',
    label: 'Adriatische kust ↔ laarsvorm verward',
    module: 'Topo',
    description:
      'Leerling verwart de gebogen kustlijn van Kroatië aan de Adriatische Zee met de laarsvorm van Italië.',
    typicalFamilies: ['TOPO-EU-LAND'],
  },
  // Q002 — NIET-vraag over buurlanden van Nederland
  {
    id: 'topo.eu-niet-vraag-buurland-oost',
    label: 'NIET-vraag genegeerd, kiest oostelijk buurland',
    module: 'Topo',
    description:
      'Leerling negeert het sleutelwoord "NIET" en kiest Duitsland (een werkelijk buurland in het oosten) als antwoord op de vraag welk land NIET aan Nederland grenst.',
    typicalFamilies: ['TOPO-EU-LAND', 'TOPO-NL-BUURLANDEN'],
  },
  {
    id: 'topo.eu-niet-vraag-buurland-zuid',
    label: 'NIET-vraag genegeerd, kiest zuidelijk buurland',
    module: 'Topo',
    description:
      'Leerling negeert het sleutelwoord "NIET" en kiest België (een werkelijk buurland in het zuiden) als antwoord op de vraag welk land NIET aan Nederland grenst.',
    typicalFamilies: ['TOPO-EU-LAND', 'TOPO-NL-BUURLANDEN'],
  },
  {
    id: 'topo.eu-alle-genoemd-als-buurland',
    label: 'Denkt dat alle genoemde landen buurland zijn',
    module: 'Topo',
    description:
      'Leerling kiest "geen van alle" en gaat ervan uit dat elk in de vraag genoemd land aan Nederland grenst — herkent niet dat Frankrijk geen direct buurland is.',
    typicalFamilies: ['TOPO-EU-LAND', 'TOPO-NL-BUURLANDEN'],
  },
  // Q003 — grootste EU-land qua oppervlakte
  {
    id: 'topo.eu-grootte-spanje-eerst',
    label: 'Verwart Spanje (2e) met Frankrijk (1e) qua oppervlakte',
    module: 'Topo',
    description:
      'Leerling kiest Spanje als grootste EU-land qua oppervlakte; Spanje is wel groot (~505.000 km²) maar Frankrijk is groter (~640.000 km²).',
    typicalFamilies: ['TOPO-EU-LAND'],
  },
  {
    id: 'topo.eu-grootte-inwoners-vs-opp',
    label: 'Verwart meeste inwoners (Duitsland) met grootste oppervlakte',
    module: 'Topo',
    description:
      'Leerling koppelt "grootste land" aan inwonertal in plaats van aan oppervlakte: Duitsland heeft de meeste inwoners van de EU, maar Frankrijk en Spanje zijn qua oppervlakte groter.',
    typicalFamilies: ['TOPO-EU-LAND'],
  },
  {
    id: 'topo.eu-grootte-italie-overschat',
    label: 'Overschat oppervlakte Italië door herkenbare laarsvorm',
    module: 'Topo',
    description:
      'Leerling overschat de oppervlakte van Italië omdat de laarsvorm visueel opvalt op de kaart; Italië is met ~301.000 km² juist kleiner dan Frankrijk, Spanje en Duitsland.',
    typicalFamilies: ['TOPO-EU-LAND'],
  },
  // Q004 — Polen in welke regio van Europa
  {
    id: 'topo.eu-regio-polen-in-west',
    label: 'Plaatst Polen in West-Europa i.p.v. Oost-Europa',
    module: 'Topo',
    description:
      'Leerling plaatst Polen in West-Europa; Polen ligt juist ten oosten van Duitsland en hoort dus tot Oost-Europa.',
    typicalFamilies: ['TOPO-EU-LAND'],
  },
  {
    id: 'topo.eu-regio-polen-in-zuid',
    label: 'Plaatst Polen in Zuid-Europa i.p.v. Oost-Europa',
    module: 'Topo',
    description:
      'Leerling plaatst Polen in Zuid-Europa; Zuid-Europa omvat landen rond de Middellandse Zee, terwijl Polen ten oosten van Duitsland ligt.',
    typicalFamilies: ['TOPO-EU-LAND'],
  },
  {
    id: 'topo.eu-regio-polen-in-noord',
    label: 'Plaatst Polen in Noord-Europa i.p.v. Oost-Europa',
    module: 'Topo',
    description:
      'Leerling plaatst Polen in Noord-Europa; Noord-Europa = Scandinavië (Noorwegen, Zweden, Finland, Denemarken), terwijl Polen zuidelijker en oostelijker ligt.',
    typicalFamilies: ['TOPO-EU-LAND'],
  },
  // Q005 — Iberisch Schiereiland (Spanje + Portugal)
  {
    id: 'topo.eu-schiereiland-frankrijk-iberisch',
    label: 'Denkt dat Frankrijk op het Iberisch Schiereiland ligt',
    module: 'Topo',
    description:
      'Leerling rekent Frankrijk tot het Iberisch Schiereiland en mist dat de Pyreneeën de natuurlijke noordgrens vormen — Frankrijk ligt ten noorden, Spanje ten zuiden.',
    typicalFamilies: ['TOPO-EU-LAND'],
  },
  {
    id: 'topo.eu-schiereiland-italie-iberisch',
    label: 'Verwart Apennijns/Balkan met Iberisch Schiereiland',
    module: 'Topo',
    description:
      'Leerling verwart het Apennijns Schiereiland (Italië) of het Balkanschiereiland (Griekenland) met het Iberisch Schiereiland (Spanje + Portugal).',
    typicalFamilies: ['TOPO-EU-LAND'],
  },
  {
    id: 'topo.eu-schiereiland-scandinavisch-iberisch',
    label: 'Verwart Scandinavisch met Iberisch Schiereiland',
    module: 'Topo',
    description:
      'Leerling koppelt Noorwegen en Zweden (Scandinavisch Schiereiland, Noord-Europa) aan het Iberisch Schiereiland (Zuidwest-Europa).',
    typicalFamilies: ['TOPO-EU-LAND'],
  },
  // Q006 — IJsland als eilandland ver in de Atlantische Oceaan
  {
    id: 'topo.eu-eiland-ierland-vs-ijsland',
    label: 'Verwart Ierland (dichtbij vasteland) met IJsland (ver weg)',
    module: 'Topo',
    description:
      'Leerling kiest Ierland als het "verre" eilandland in de Atlantische Oceaan; Ierland ligt juist dicht bij Groot-Brittannië en het Europese vasteland, IJsland ligt halverwege Europa en Noord-Amerika.',
    typicalFamilies: ['TOPO-EU-LAND'],
  },
  {
    id: 'topo.eu-eiland-gb-vs-ijsland',
    label: 'Verwart Groot-Brittannië (dichtbij) met IJsland (ver weg)',
    module: 'Topo',
    description:
      'Leerling kiest Groot-Brittannië als het "verre" eilandland; Groot-Brittannië ligt vlak bij Frankrijk (het Kanaal is ~34 km breed), IJsland ligt veel verder in de Atlantische Oceaan.',
    typicalFamilies: ['TOPO-EU-LAND'],
  },
  {
    id: 'topo.eu-eiland-sicilie-als-land',
    label: 'Behandelt Sicilië als zelfstandig eilandland',
    module: 'Topo',
    description:
      'Leerling behandelt Sicilië als een zelfstandig eilandland; Sicilië is een eiland van Italië en ligt bovendien in de Middellandse Zee, niet in de Atlantische Oceaan.',
    typicalFamilies: ['TOPO-EU-LAND'],
  },
]
